// /set-password#lat_pl_…: a person sets their own password from the link an
// admin sent them, and is signed in (routes/auth_password.py).
//
// The token is in the URL's fragment, which a browser never sends: it is
// read once into memory and stripped from the address bar at once, so it
// stays out of history, bookmarks and screenshots. The link is checked first
// (nothing spent), then spent by the one call that sets the password; a
// refused password spends nothing. Rendered before the auth gate (App.tsx),
// like /login-assist, and always opened in Safari, never in the iPhone app
// (companion/nativePaths.ts).

import { useEffect, useRef, useState, type FormEvent } from "react";
import { AlertCircle } from "lucide-react";

import { checkPasswordLink, setPasswordFromLink, type PasswordLinkCheck } from "@/api/passwords";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  linkErrorText,
  passwordCode,
  passwordProblemText,
  setPasswordTitle,
  tokenFromHash,
} from "@/features/admin/accounts/accountModel";
import { ApiError, errorCode, errorText } from "@/lib/api";
import { useAuth } from "@/stores/auth";
import { ambientLight } from "@/theme";

import { PasswordInput } from "./PasswordInput";

type State =
  | { kind: "checking" }
  | { kind: "ready"; link: PasswordLinkCheck }
  | { kind: "dead"; message: string };

const LINK_CODES = new Set(["link_unknown", "link_used", "link_void", "link_expired", "login_gone"]);

function linkFailure(e: unknown): string | null {
  const code = errorCode(e);
  if (code && LINK_CODES.has(code)) return linkErrorText(code);
  if (e instanceof ApiError && (e.status === 404 || e.status === 410)) return linkErrorText(code);
  return null;
}

export function SetPassword() {
  // Read once (the effect below strips it from the address bar).
  const [token] = useState(() => tokenFromHash(window.location.hash));
  const [state, setState] = useState<State>(token ? { kind: "checking" } : { kind: "dead", message: linkErrorText(null) });
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const passRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  useEffect(() => {
    if (!token) return;
    let live = true;
    checkPasswordLink(token).then(
      (link) => live && setState({ kind: "ready", link }),
      (e: unknown) => live && setState({ kind: "dead", message: linkFailure(e) ?? errorText(e) }),
    );
    return () => {
      live = false;
    };
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (state.kind !== "ready" || !token) return;
    setError("");
    const code = passwordCode(state.link.username, password);
    if (code) {
      setFieldError(passwordProblemText(code));
      passRef.current?.focus();
      return;
    }
    setSaving(true);
    try {
      const session = await setPasswordFromLink(token, password);
      useAuth.getState().login(session.user, session.customerId, session.role, session.agent ?? null);
      window.location.replace("/");
    } catch (err: unknown) {
      setSaving(false);
      const dead = linkFailure(err);
      if (dead) setState({ kind: "dead", message: dead });
      else if (err instanceof ApiError && err.status === 422) setFieldError(passwordProblemText(errorCode(err)));
      else setError(errorText(err));
    }
  }

  const title = state.kind === "ready" ? setPasswordTitle(state.link.purpose) : "Set your password";

  return (
    <div className="flex app-vh w-full flex-col items-center overflow-y-auto pt-safe pb-safe" style={{ background: ambientLight }}>
      <main className="w-full max-w-[432px] px-4 py-12 sm:my-auto sm:py-10">
        <div className="sm:rounded-card sm:border sm:border-line sm:bg-panel sm:p-8 sm:shadow-[var(--shadow-panel)]">
          <img src="/corvus-logo.svg" alt="Corvus Research" className="h-10 w-auto" />
          <h1 className="mt-8 font-display text-2xl tracking-[var(--font-display-tracking)] text-fg">{title}</h1>

          {state.kind === "checking" && (
            <p role="status" className="mt-2 text-sm text-fg-muted">
              Checking your link…
            </p>
          )}

          {state.kind === "dead" && (
            <>
              <p role="alert" className="mt-3 flex items-start gap-2 text-base text-fg">
                <AlertCircle aria-hidden className="mt-1 h-4 w-4 shrink-0 text-danger" />
                {state.message}
              </p>
              <Button className="mt-6 w-full" size="lg" variant="secondary" onClick={() => window.location.replace("/")}>
                Go to sign in
              </Button>
            </>
          )}

          {state.kind === "ready" && (
            <form onSubmit={submit} noValidate className="mt-6 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sp-username" className="text-sm font-medium text-fg">
                  Username
                </label>
                <Input id="sp-username" fieldSize="lg" value={state.link.username} readOnly autoComplete="username" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sp-password" className="text-sm font-medium text-fg">
                  Password
                </label>
                <PasswordInput
                  ref={passRef}
                  id="sp-password"
                  fieldSize="lg"
                  autoComplete="new-password"
                  autoFocus
                  enterKeyHint="go"
                  value={password}
                  disabled={saving}
                  onChange={(ev) => {
                    setPassword(ev.target.value);
                    if (fieldError) setFieldError("");
                  }}
                  aria-invalid={!!fieldError}
                  aria-describedby={fieldError ? "sp-password-error" : undefined}
                />
                {fieldError && (
                  <p id="sp-password-error" className="text-sm text-danger">
                    {fieldError}
                  </p>
                )}
              </div>
              <Button type="submit" size="lg" loading={saving} className="mt-2 w-full">
                Set password
              </Button>
              {error && (
                <p role="alert" className="flex items-start gap-2 text-sm text-danger">
                  <AlertCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                  {error}
                </p>
              )}
            </form>
          )}
        </div>
        <p className="mt-8 text-center text-xs text-fg-subtle">Corvus Agent Platform</p>
      </main>
    </div>
  );
}
